import { useState } from 'react';
import { Badge, Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ApprovalRuleOverrideType,
  type CompanyApprovalConfigurationModel
} from 'src/features/company/approvals/types/approvalConfigTypes';
import {
  doesContactTagOverrideApply,
  formatOverrideType,
  getMatchingContactTagValues,
  getOverrideDetail,
  type RuleOverrideScope
} from '../utils/approvalOverrideDisplay';

type TravellerApprovalOverrideItem = {
  ruleName: string;
  override: RuleOverrideScope;
  reason: string;
};

type Props = {
  config: CompanyApprovalConfigurationModel | undefined;
  travellerId: number;
  travellerTags?: string[];
};

export const TravellerApprovalOverrideInfo = ({ config, travellerId, travellerTags }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const items = getTravellerApprovalOverrideItems(config, travellerId, travellerTags);

  if (items.length === 0) {
    return null;
  }

  return (
    <Box
      borderLeft='3px solid'
      borderColor='blue.200'
      borderRadius='sm'
      bg='blue.50'
      px='8px'
      py='6px'
      marginTop={'8px'}>
      <Button
        type='button'
        variant='plain'
        w='100%'
        h='auto'
        minH='auto'
        p='0'
        justifyContent='space-between'
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded((current) => !current)}>
        <Text fontSize='xs' fontWeight='semibold' color='blue.800'>
          Approval Info
        </Text>
        <HStack gap='4px' color='blue.700'>
          <Text fontSize='2xs' fontWeight='semibold'>
            {isExpanded ? 'Hide' : `Show (${items.length})`}
          </Text>
          <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} size='xs' />
        </HStack>
      </Button>
      {isExpanded && (
        <VStack align='stretch' gap='6px' mt='6px'>
          {items.map((item, index) => {
            const overrideDetail =
              item.override.OverrideType === ApprovalRuleOverrideType.ContactTags
                ? {
                    values: getMatchingContactTagValues(item.override.ContactTags, travellerTags),
                    emptyText: 'No contact tags selected.'
                  }
                : getOverrideDetail(item.override);

            return (
              <Box key={`${item.ruleName}-${item.override.OverrideType}-${index}`}>
                <HStack gap='4px' flexWrap='wrap' mb='3px'>
                  <Badge colorPalette='blue' variant='subtle'>
                    {item.ruleName}
                  </Badge>
                  <Badge colorPalette='gray' variant='outline'>
                    {formatOverrideType(item.override.OverrideType)}
                  </Badge>
                </HStack>
                <Text fontSize='2xs' color='gray.700' mb='3px'>
                  {item.reason}
                </Text>
                {overrideDetail.values.length > 0 ? (
                  <HStack gap='4px' flexWrap='wrap'>
                    {overrideDetail.values.map((value, valueIndex) => (
                      <Badge key={`${value}-${valueIndex}`} colorPalette='gray' variant='subtle'>
                        {value}
                      </Badge>
                    ))}
                  </HStack>
                ) : (
                  <Text fontSize='2xs' color='gray.600'>
                    {overrideDetail.emptyText}
                  </Text>
                )}
              </Box>
            );
          })}
        </VStack>
      )}
    </Box>
  );
};

function getTravellerApprovalOverrideItems(
  config: CompanyApprovalConfigurationModel | undefined,
  travellerId: number,
  travellerTags: string[] | undefined
): TravellerApprovalOverrideItem[] {
  if (!config) {
    return [];
  }

  return [
    ...getContactRelatedOverrideItems(
      'Rate Cap',
      config.RateCapRuleConfigurationModel.Overrides,
      travellerId,
      travellerTags
    ),
    ...getContactRelatedOverrideItems(
      'Self-Booking Requires Approval',
      config.SelfBookingRequiresApprovalConfigurationModel.Overrides,
      travellerId,
      travellerTags
    )
  ];
}

function getContactRelatedOverrideItems(
  ruleName: string,
  overrides: RuleOverrideScope[] | undefined,
  travellerId: number,
  travellerTags: string[] | undefined
): TravellerApprovalOverrideItem[] {
  return (overrides ?? []).flatMap((override) => {
    if (override.OverrideType === ApprovalRuleOverrideType.Contacts && override.ContactIds?.includes(travellerId)) {
      return [
        {
          ruleName,
          override,
          reason: 'This traveler is selected in a contact-specific approval override.'
        }
      ];
    }

    if (
      override.OverrideType === ApprovalRuleOverrideType.ContactTags &&
      doesContactTagOverrideApply(override.ContactTags, travellerTags)
    ) {
      return [
        {
          ruleName,
          override,
          reason: 'This traveler has a contact tag that matches a contact-tag approval override.'
        }
      ];
    }

    return [];
  });
}
